import { useCallback, useEffect, useRef, useState } from "react";
import type { RoundFeedPage } from "../../../shared/contracts/round-feed.js";

export function useRoundFeed(revision: string) {
  const [page, setPage] = useState<RoundFeedPage>({
    items: [],
    nextCursor: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const busy = useRef(false);
  const load = useCallback(async (cursor: string | null = null) => {
    if (busy.current) return;
    busy.current = true;
    const requested = generation.current;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `/api/v1/feed/rounds${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Results could not load. Try again.");
      const next = (await response.json()) as RoundFeedPage;
      if (requested !== generation.current) return;
      setPage((current) => ({
        nextCursor: next.nextCursor,
        items: cursor
          ? [
              ...current.items,
              ...next.items.filter(
                (n) =>
                  !current.items.some((p) => p.card.roundId === n.card.roundId),
              ),
            ]
          : next.items,
      }));
    } catch (failure) {
      if (requested === generation.current)
        setError(
          failure instanceof Error
            ? failure.message
            : "Results could not load.",
        );
    } finally {
      if (requested === generation.current) {
        busy.current = false;
        setLoading(false);
      }
    }
  }, []);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "hidden") return;
      generation.current++;
      busy.current = false;
      setPage({ items: [], nextCursor: null });
      void load();
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      generation.current++;
      busy.current = false;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [revision, load]);
  return {
    ...page,
    loading,
    error,
    retry: () => void load(),
    more: () => void load(page.nextCursor),
  };
}
